import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlignLeft, ArrowLeft, ArrowRight, Volume2, RotateCcw,
  CheckCircle2, Loader2, Trophy, ChevronRight, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { AmbientCanvas } from "@/components/effects/ambient-canvas";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import {
  fetchTopics, fetchTopicInfo, fetchSentences, fetchTopicMastery,
  saveProgress, submitQuizSession,
  resolveStartTopic, nextTopicAfter, setRecentTopicId,
  type Topic, type SentenceRow
} from "@/lib/queries";
import { playWordClick } from "@/lib/audio";
import { playCorrect, playWrong, playReveal, playSessionEnd } from "@/lib/sounds";
import { flyStarsToPill, confettiForResult } from "@/lib/effects";
import { cn } from "@/lib/utils";

const ROUND_SIZE = 5;
const POINTS_PER_CORRECT = 5;   // matches legacy server's 'sentence' rate

type Phase = "loading" | "no-topics" | "no-sentences" | "playing" | "results";
type Token = { uid: string; text: string };

export default function SentenceBuilder() {
  const [params] = useSearchParams();
  const urlTopic = params.get("topic");
  const nav = useNavigate();

  const [phase, setPhase] = React.useState<Phase>("loading");
  const [topic, setTopic] = React.useState<Topic | null>(null);
  const [topics, setTopics] = React.useState<Topic[]>([]);

  const [pool, setPool] = React.useState<SentenceRow[]>([]);
  const playedRef = React.useRef<Set<string | number>>(new Set());
  const [current, setCurrent] = React.useState<SentenceRow | null>(null);
  const [bank, setBank]   = React.useState<Token[]>([]);
  const [build, setBuild] = React.useState<Token[]>([]);

  const [attempts, setAttempts]       = React.useState(0);
  const [solved, setSolved]           = React.useState(0);
  const [feedback, setFeedback]       = React.useState<null | "ok" | "no">(null);
  const startRef = React.useRef<Date>(new Date());

  const [submitting, setSubmitting] = React.useState(false);
  const [awarded, setAwarded] = React.useState<{ new_total: number; points_awarded: number } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase("loading");
      const [allTopics, mast] = await Promise.all([fetchTopics(), fetchTopicMastery()]);
      if (cancelled) return;
      setTopics(allTopics);

      let resolved: Topic | null = null;
      if (urlTopic) resolved = await fetchTopicInfo(urlTopic);
      if (!resolved) resolved = await resolveStartTopic(allTopics, mast);
      if (!resolved) { setPhase("no-topics"); return; }

      setRecentTopicId(resolved.id);
      const sentences = await fetchSentences(resolved.id);
      if (cancelled) return;

      setTopic(resolved);
      setPool(sentences);
      playedRef.current = new Set();
      setAttempts(0);
      setSolved(0);
      setFeedback(null);
      startRef.current = new Date();

      if (sentences.length === 0) { setPhase("no-sentences"); return; }
      setPhase("playing");
      pickNext(sentences, new Set());
    })();
    return () => { cancelled = true; };
  }, [urlTopic]);

  function pickNext(p: SentenceRow[], played: Set<string | number>) {
    const remaining = p.filter(s => !played.has(s.id));
    const list = remaining.length ? remaining : p;
    const chosen = list[Math.floor(Math.random() * list.length)];
    playedRef.current = new Set(played);
    playedRef.current.add(chosen.id);
    setCurrent(chosen);

    const tokens = String(chosen.arabic || "").trim().split(/\s+/).filter(Boolean);
    let uid = 0;
    const shuffled = shuffle(tokens.map(text => ({ uid: `t-${++uid}`, text })));
    setBank(shuffled);
    setBuild([]);
    setFeedback(null);
    setAttempts(a => a + 1);
  }

  function reset() {
    if (!current) return;
    setBank(shuffle([...bank, ...build]));
    setBuild([]);
    setFeedback(null);
  }

  function moveTokenToBuild(uid: string) {
    const t = bank.find(t => t.uid === uid);
    if (!t) return;
    setBank(bank.filter(b => b.uid !== uid));
    setBuild([...build, t]);
    setFeedback(null);
  }
  function moveTokenToBank(uid: string) {
    const t = build.find(t => t.uid === uid);
    if (!t) return;
    setBuild(build.filter(b => b.uid !== uid));
    setBank([...bank, t]);
    setFeedback(null);
  }
  function insertAt(uid: string, target: "build" | "bank", from: "build" | "bank", idx: number) {
    const sourceArr = from === "bank" ? bank : build;
    const t = sourceArr.find(t => t.uid === uid);
    if (!t) return;
    const removed = sourceArr.filter(x => x.uid !== uid);
    const destArr = target === "bank" ? (from === "bank" ? removed : [...bank]) : (from === "build" ? removed : [...build]);
    const clamped = Math.max(0, Math.min(idx, destArr.length));
    destArr.splice(clamped, 0, t);
    if (target === "bank") {
      setBank(destArr);
      if (from === "build") setBuild(removed);
    } else {
      setBuild(destArr);
      if (from === "bank") setBank(removed);
    }
    setFeedback(null);
  }

  function check(btn?: HTMLElement | null) {
    if (!current) return;
    if (build.length === 0) { setFeedback("no"); playWrong(); return; }
    const expected = String(current.arabic).trim().split(/\s+/).filter(Boolean).join(" ");
    const built    = build.map(t => t.text).join(" ").trim();
    if (built === expected) {
      setFeedback("ok");
      setSolved(s => s + 1);
      playCorrect();
      if (btn) flyStarsToPill(btn);
      void saveProgress({
        word_id: `s:${current.id}`, correct: true, mode: "sentence", points: POINTS_PER_CORRECT
      });
    } else {
      setFeedback("no");
      playWrong();
      void saveProgress({ word_id: `s:${current.id}`, correct: false, mode: "sentence" });
    }
  }

  function advance() {
    if (attempts >= ROUND_SIZE) { void endRound(); return; }
    playReveal();
    pickNext(pool, playedRef.current);
  }

  async function endRound() {
    setPhase("results");
    setSubmitting(true);
    playSessionEnd(solved, ROUND_SIZE);
    window.setTimeout(() => {
      const card = document.querySelector("[data-results-card]");
      confettiForResult({ correct: solved, total: ROUND_SIZE, originEl: card });
    }, 80);
    try {
      const r = await submitQuizSession({
        claimed_correct: solved,
        session_started: startRef.current,
        mode:            "sentence"
      });
      if (r) setAwarded(r);
    } catch (e) { console.warn("[sb] submit", e); }
    setSubmitting(false);
  }

  function goNextTopic() {
    if (!topic) return;
    const next = nextTopicAfter(topic.id, topics);
    if (next) {
      setRecentTopicId(next.id);
      nav(`/sentence-builder?topic=${encodeURIComponent(next.id)}`);
    }
  }

  const nextTopic = topic ? nextTopicAfter(topic.id, topics) : null;
  const pct = ROUND_SIZE > 0 ? Math.round((solved / ROUND_SIZE) * 100) : 0;

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <div className="relative">
        <AmbientCanvas className="absolute inset-0 -z-10 w-full h-[900px] pointer-events-none" density={40} />

        <div className="relative mx-auto max-w-3xl px-4 md:px-6 pt-10 pb-32">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard"><ArrowLeft className="w-4 h-4" /> Back</Link>
            </Button>
            {phase === "playing" && <Badge variant="accent">Sentence {attempts} / {ROUND_SIZE}</Badge>}
          </div>

          <BlurReveal>
            <div className="flex items-center gap-3 mb-2">
              <AlignLeft className="w-6 h-6 text-accent" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Sentence Builder</h1>
            </div>
            {topic && (
              <p className="text-fg-2">
                <span className="text-accent">{topic.name_en}</span>
                <span className="text-fg-3"> · {pool.length} sentences</span>
              </p>
            )}
          </BlurReveal>

          <div className="mt-8">
            {phase === "loading"      && <Spinner />}
            {phase === "no-topics"    && <NoCard message="No topics yet." />}
            {phase === "no-sentences" && <NoCard message="No sentences for this topic yet — try Learn or Quiz instead." />}

            {phase === "playing" && current && (
              <BlurReveal>
                <Card>
                  <CardContent>
                    <div className="text-xs uppercase tracking-widest text-fg-3">Translate to Arabic</div>
                    <div className="mt-2 text-xl font-semibold">{current.english}</div>

                    <Separator className="my-5" />

                    {/* Build area */}
                    <div
                      dir="rtl"
                      className="rounded-2xl border-2 border-dashed border-line bg-bg p-5 min-h-[100px] flex flex-wrap gap-2 items-center"
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => {
                        e.preventDefault();
                        const payload = e.dataTransfer.getData("text/plain");
                        const [uid, from] = payload.split("|");
                        if (uid && (from === "bank" || from === "build")) {
                          insertAt(uid, "build", from, build.length);
                        }
                      }}
                    >
                      {build.length === 0 && (
                        <span className="text-fg-3 text-sm">Drop or tap words here to build the sentence.</span>
                      )}
                      {build.map(t => (
                        <SBToken key={t.uid} token={t} where="build" onClick={() => moveTokenToBank(t.uid)} />
                      ))}
                    </div>

                    {/* Bank */}
                    <div className="mt-5">
                      <div className="text-xs uppercase tracking-widest text-fg-3 mb-2">Words</div>
                      <div
                        dir="rtl"
                        className="rounded-2xl border border-line bg-bg-2/40 p-4 min-h-[80px] flex flex-wrap gap-2"
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                          e.preventDefault();
                          const payload = e.dataTransfer.getData("text/plain");
                          const [uid, from] = payload.split("|");
                          if (uid && from === "build") moveTokenToBank(uid);
                        }}
                      >
                        {bank.length === 0 && <span className="text-fg-3 text-sm">All words used.</span>}
                        {bank.map(t => (
                          <SBToken key={t.uid} token={t} where="bank" onClick={() => moveTokenToBuild(t.uid)} />
                        ))}
                      </div>
                    </div>

                    {feedback === "ok" && (
                      <div className="mt-5 rounded-xl border border-ok/40 bg-ok/10 text-ok px-4 py-3 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" /> Correct! +{POINTS_PER_CORRECT} pts queued.
                      </div>
                    )}
                    {feedback === "no" && (
                      <div className="mt-5 rounded-xl border border-bad/40 bg-bad/10 text-bad px-4 py-3 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> Not quite — try rearranging.
                      </div>
                    )}

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <Button variant="ghost" size="md" onClick={reset}><RotateCcw className="w-4 h-4" /> Reset</Button>
                      {feedback === "ok" && (
                        <Button variant="outline" size="md" onClick={() => playWordClick({ arabic: current.arabic, transliteration: current.english })}>
                          <Volume2 className="w-4 h-4" /> Hear it
                        </Button>
                      )}
                      <div className="ml-auto flex gap-2">
                        {feedback === "ok" ? (
                          <Button size="md" onClick={advance}>
                            {attempts >= ROUND_SIZE ? <>Finish round <Trophy className="w-4 h-4" /></> : <>Next <ArrowRight className="w-4 h-4" /></>}
                          </Button>
                        ) : (
                          <Button size="md" onClick={e => check(e.currentTarget)}>Check answer <ArrowRight className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </BlurReveal>
            )}

            {phase === "results" && (
              <BlurReveal>
                <Card data-results-card>
                  <CardContent className="text-center py-10">
                    <div className="text-xs uppercase tracking-widest text-fg-3">Round complete</div>
                    <h2 className="text-4xl font-bold tracking-tight mt-2">
                      {solved} / {ROUND_SIZE} <span className="text-fg-3 font-normal">correct</span>
                    </h2>

                    {submitting ? (
                      <p className="mt-4 text-fg-2 inline-flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-accent" /> Banking points…
                      </p>
                    ) : awarded ? (
                      <div className="mt-5 inline-flex items-center gap-3 rounded-2xl border border-accent-lo bg-accent/10 px-5 py-3">
                        <Trophy className="w-5 h-5 text-accent" />
                        <span className="text-accent font-bold text-xl">+{awarded.points_awarded.toLocaleString()}</span>
                        <span className="text-xs uppercase tracking-widest text-fg-3">points</span>
                        <span className="text-xs text-fg-2">· new total {awarded.new_total.toLocaleString()}</span>
                      </div>
                    ) : (
                      <p className="mt-4 text-fg-2 text-sm">Points couldn't sync — try again in a moment.</p>
                    )}

                    <div className="mt-7 flex flex-wrap gap-3 justify-center">
                      {pct >= 80 && nextTopic ? (
                        <>
                          <Button size="lg" onClick={goNextTopic}>
                            Next topic: {nextTopic.name_en} <ChevronRight className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="lg" onClick={() => nav(0)}><RotateCcw className="w-4 h-4" /> Again</Button>
                        </>
                      ) : (
                        <>
                          <Button size="lg" onClick={() => nav(0)}><RotateCcw className="w-4 h-4" /> Try again</Button>
                          {nextTopic && (
                            <Button variant="outline" size="lg" onClick={goNextTopic}>Skip to next <ChevronRight className="w-4 h-4" /></Button>
                          )}
                          <Button variant="ghost" size="lg" asChild><Link to="/dashboard">Dashboard <ArrowRight className="w-4 h-4" /></Link></Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </BlurReveal>
            )}
          </div>
        </div>
      </div>
    </PageFrame>
  );
}

function SBToken({ token, where, onClick }: { token: Token; where: "bank" | "build"; onClick: () => void }) {
  return (
    <button
      type="button"
      draggable
      onDragStart={e => { e.dataTransfer.setData("text/plain", `${token.uid}|${where}`); e.dataTransfer.effectAllowed = "move"; }}
      onClick={onClick}
      className={cn(
        "px-4 py-2 rounded-full text-lg transition-colors cursor-grab active:cursor-grabbing select-none",
        where === "build"
          ? "border border-accent-lo bg-accent/15 text-accent font-medium"
          : "border border-line bg-bg-2 hover:bg-bg-3 hover:border-line-2"
      )}
      style={{ fontFamily: "'Noto Sans Arabic',serif" }}
    >
      {token.text}
    </button>
  );
}

function NoCard({ message }: { message: string }) {
  return (
    <Card className="text-center">
      <CardContent className="py-12"><p className="text-fg-2">{message}</p></CardContent>
    </Card>
  );
}

function Spinner() {
  return (
    <div className="py-16 flex items-center justify-center gap-2 text-fg-3">
      <Loader2 className="w-4 h-4 animate-spin text-accent" /> Loading…
    </div>
  );
}

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
